import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { wiki, c, fadeIn, SkeletonLine } from './wikiStyles';
import { useWikiConfig, useWikiManifest, useAdminState, slugToTitle } from './useWiki';

const ITEMS_PER_PAGE = 200;

/* ── Fuzzy Search Utilities ────────────────────────────── */

function stripDiacritics(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function searchNormalize(str) {
  return stripDiacritics(str)
    .toLowerCase()
    .replace(/sh/g, 's')
    .replace(/chh/g, 'c')
    .replace(/ch/g, 'c')
    .replace(/th/g, 't')
    .replace(/kh/g, 'k')
    .replace(/ph/g, 'p')
    .replace(/bh/g, 'b')
    .replace(/dh/g, 'd')
    .replace(/gh/g, 'g')
    .replace(/jh/g, 'j')
    .replace(/aa/g, 'a')
    .replace(/ee/g, 'i')
    .replace(/oo/g, 'u')
    .replace(/[_\-\s.]+/g, '');
}

function subsequenceScore(query, target) {
  let qi = 0;
  let score = 0;
  let prevIdx = -2;
  for (let ti = 0; ti < target.length && qi < query.length; ti++) {
    if (target[ti] === query[qi]) {
      score += ti === prevIdx + 1 ? 3 : 1;
      if (ti === 0 || /[_\s]/.test(target[ti - 1])) score += 2;
      prevIdx = ti;
      qi++;
    }
  }
  return qi === query.length ? score : 0;
}

function editDistance(a, b) {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  let curr = new Array(n + 1);
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      curr[j] =
        a[i - 1] === b[j - 1]
          ? prev[j - 1]
          : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

function computeScore(qLower, qNorm, item) {
  if (item._titleLower.includes(qLower)) return 1000;
  if (item._slugLower.includes(qLower)) return 950;

  if (item._normTitle.includes(qNorm)) return 800;
  if (item._normSlug.includes(qNorm)) return 750;

  const subScore = subsequenceScore(qNorm, item._normTitle);
  if (subScore > 0) return 100 + subScore;

  const subScoreSlug = subsequenceScore(qNorm, item._normSlug);
  if (subScoreSlug > 0) return 80 + subScoreSlug;

  if (qNorm.length >= 3 && qNorm.length <= 12) {
    const maxThreshold = qNorm.length <= 4 ? 1 : qNorm.length <= 7 ? 2 : 3;
    const words = item._normTitle.split(/[_\s]+/);
    for (const word of words) {
      const prefix = word.substring(0, qNorm.length + 2);
      if (editDistance(qNorm, prefix) <= maxThreshold) return 50;
    }
    const titlePrefix = item._normTitle.substring(0, qNorm.length + 2);
    if (editDistance(qNorm, titlePrefix) <= maxThreshold) return 45;
  }

  return 0;
}

/* ── Styled Components ──────────────────────────────────── */

const Hero = styled.section`
  text-align: center;
  padding: 4.5rem 2rem 2.5rem;

  @media (max-width: 768px) {
    padding: 3rem 1.25rem 2rem;
  }
`;

const HeroMark = styled.div`
  color: ${c.accent};
  font-size: 1.5rem;
  margin-bottom: 1.25rem;
  opacity: 0.8;
`;

const HeroTitle = styled.h1`
  font-family: ${wiki.fonts.heading};
  font-size: 2.75rem;
  font-weight: 700;
  color: ${c.text};
  margin-bottom: 0.75rem;
  letter-spacing: -0.02em;
  line-height: 1.15;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const HeroSub = styled.p`
  font-family: ${wiki.fonts.body};
  font-size: 1.15rem;
  color: ${c.textSec};
  max-width: 520px;
  margin: 0 auto 1.5rem;
  line-height: 1.6;

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const ArticleCount = styled.span`
  font-family: ${wiki.fonts.ui};
  font-size: 0.8rem;
  color: ${c.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

/* ── Search ─────────────────────────────────────────────── */

const SearchWrap = styled.div`
  max-width: 520px;
  margin: 0 auto 2.5rem;
  padding: 0 2rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 0 1.25rem;
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 2.9rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${c.textMuted};
  font-size: 1rem;
  pointer-events: none;

  @media (max-width: 768px) {
    left: 2.15rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.85rem 1rem 0.85rem 2.5rem;
  font-family: ${wiki.fonts.ui};
  font-size: 0.95rem;
  color: ${c.text};
  background: ${c.bgWhite};
  border: 1px solid ${c.border};
  border-radius: ${wiki.radius};
  outline: none;
  transition: all 0.25s;

  &::placeholder {
    color: ${c.textMuted};
  }

  &:focus {
    border-color: ${c.accent};
    box-shadow: 0 0 0 3px ${c.accentSoft};
  }
`;

const ClearBtn = styled.button`
  position: absolute;
  right: 2.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 1.25rem;
  color: ${c.textMuted};
  cursor: pointer;
  padding: 0.2rem 0.4rem;
  line-height: 1;

  &:hover {
    color: ${c.text};
  }

  @media (max-width: 768px) {
    right: 2rem;
  }
`;

const ResultCount = styled.p`
  text-align: center;
  font-family: ${wiki.fonts.ui};
  font-size: 0.8rem;
  color: ${c.textMuted};
  margin-bottom: 1.5rem;
`;

/* ── Alphabet Bar ───────────────────────────────────────── */

const AlphabetBar = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.75rem 2rem;
  margin-bottom: 2rem;
  position: sticky;
  top: 60px;
  z-index: 50;
  background: ${c.bg};
  border-bottom: 1px solid ${c.borderLight};

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    gap: 0.15rem;
  }
`;

const LetterLink = styled.button`
  font-family: ${wiki.fonts.ui};
  font-size: 0.78rem;
  font-weight: 600;
  color: ${(p) => (p.$active ? c.bgWhite : c.textSec)};
  background: ${(p) => (p.$active ? c.accent : 'none')};
  text-decoration: none;
  border: none;
  cursor: pointer;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${wiki.radiusSm};
  transition: all 0.2s;

  &:hover {
    background: ${c.accent};
    color: ${c.bgWhite};
  }

  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
    font-size: 0.7rem;
  }
`;

const AllLink = styled(LetterLink)`
  width: auto;
  padding: 0 8px;
  font-size: 0.72rem;
  letter-spacing: 0.03em;
`;

/* ── Page Grid ──────────────────────────────────────────── */

const ContentArea = styled.div`
  max-width: ${wiki.maxPage};
  margin: 0 auto;
  padding: 0 2rem 4rem;
  animation: ${fadeIn} 0.4s ease;

  @media (max-width: 768px) {
    padding: 0 1.25rem 3rem;
  }
`;

const LetterGroup = styled.section`
  margin-bottom: 2.5rem;
`;

const LetterHeading = styled.h2`
  font-family: ${wiki.fonts.heading};
  font-size: 1.6rem;
  font-weight: 600;
  color: ${c.accent};
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${c.borderLight};
`;

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.75rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const PageCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1.1rem;
  background: ${c.bgWhite};
  border: 1px solid ${c.borderLight};
  border-radius: ${wiki.radiusSm};
  text-decoration: none;
  transition: all 0.25s;

  &:hover {
    border-color: ${c.accent};
    box-shadow: 0 2px 8px ${c.shadow};
    transform: translateY(-1px);
  }
`;

const CardTitle = styled.span`
  font-family: ${wiki.fonts.body};
  font-size: 0.95rem;
  color: ${c.text};
  line-height: 1.4;
  flex: 1;
`;

const CardStatusApproved = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: 700;
  background: ${c.verifiedSoft};
  color: ${c.verified};
  border: 1.5px solid ${c.verified};
`;

const CardStatusPending = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.55rem;
  color: ${c.textMuted};
  border: 1.5px dashed ${c.border};
`;

const NoResults = styled.p`
  text-align: center;
  font-family: ${wiki.fonts.body};
  font-size: 1.05rem;
  color: ${c.textMuted};
  padding: 3rem 0;
`;

const PaginationWrap = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  padding: 2rem 0 1rem;
`;

const PageBtn = styled.button`
  font-family: ${wiki.fonts.ui};
  font-size: 0.82rem;
  font-weight: ${(p) => (p.$active ? '700' : '500')};
  min-width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border-radius: ${wiki.radiusSm};
  border: 1px solid ${(p) => (p.$active ? c.accent : c.borderLight)};
  background: ${(p) => (p.$active ? c.accent : c.bgWhite)};
  color: ${(p) => (p.$active ? c.bgWhite : c.textSec)};
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: ${c.accent};
    color: ${c.accent};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const PageEllipsis = styled.span`
  font-family: ${wiki.fonts.ui};
  font-size: 0.82rem;
  color: ${c.textMuted};
  padding: 0 2px;
`;

const PageInfo = styled.p`
  text-align: center;
  font-family: ${wiki.fonts.ui};
  font-size: 0.78rem;
  color: ${c.textMuted};
  margin-top: 0.5rem;
`;

/* ── Loading Skeleton ───────────────────────────────────── */

function LoadingSkeleton() {
  return (
    <ContentArea>
      {[1, 2, 3].map((g) => (
        <LetterGroup key={g}>
          <SkeletonLine $w="40px" $h="1.5rem" $mb="1rem" />
          <PageGrid>
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonLine key={i} $h="2.8rem" $mb="0" />
            ))}
          </PageGrid>
        </LetterGroup>
      ))}
    </ContentArea>
  );
}

/* ── Component ──────────────────────────────────────────── */

function isPageProofread(slug, adminState) {
  return adminState?.approved?.[slug] === true;
}

function buildPageNumbers(current, total) {
  const pages = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }
  pages.push(1);
  if (current > 3) pages.push('...');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

export default function WikiIndex() {
  const { config } = useWikiConfig();
  const { manifest, loading, totalCount } = useWikiManifest();
  const { adminState } = useAdminState();
  const [search, setSearch] = useState('');
  const [activeLetter, setActiveLetter] = useState(null);
  const [page, setPage] = useState(1);

  const indexed = useMemo(() => {
    return manifest.map((p) => {
      const title = slugToTitle(p.slug);
      return {
        ...p,
        _title: title,
        _titleLower: title.toLowerCase(),
        _slugLower: p.slug.toLowerCase(),
        _normTitle: searchNormalize(title),
        _normSlug: searchNormalize(p.slug),
      };
    });
  }, [manifest]);

  const allLetters = useMemo(() => {
    const set = new Set();
    indexed.forEach((p) => {
      const ch = p._title?.[0]?.toUpperCase();
      if (ch) set.add(ch);
    });
    return [...set].sort();
  }, [indexed]);

  const filtered = useMemo(() => {
    let list = indexed;
    if (activeLetter) {
      list = list.filter(
        (p) => p._title?.[0]?.toUpperCase() === activeLetter,
      );
    }
    const q = search.trim();
    if (!q) return list;

    if (q.length === 1) {
      const lower = q.toLowerCase();
      return list.filter(
        (p) => p._titleLower.startsWith(lower) || p._slugLower.startsWith(lower),
      );
    }

    const qLower = q.toLowerCase();
    const qNorm = searchNormalize(q);
    const scored = [];
    for (const p of list) {
      const score = computeScore(qLower, qNorm, p);
      if (score > 0) scored.push({ ...p, _score: score });
    }
    scored.sort((a, b) => b._score - a._score);
    return scored;
  }, [indexed, search, activeLetter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages);

  const paged = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, safePage]);

  const grouped = useMemo(() => {
    const map = {};
    paged.forEach((p) => {
      const title = p._title || slugToTitle(p.slug);
      const letter = title[0]?.toUpperCase() || '#';
      if (!map[letter]) map[letter] = [];
      map[letter].push({ ...p, displayTitle: title });
    });
    return Object.keys(map)
      .sort()
      .map((letter) => ({
        letter,
        pages: map[letter].sort((a, b) =>
          a.displayTitle.localeCompare(b.displayTitle),
        ),
      }));
  }, [paged]);

  const handleLetterClick = useCallback(
    (letter) => {
      setActiveLetter((prev) => (prev === letter ? null : letter));
      setPage(1);
    },
    [],
  );

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
    setPage(1);
  }, []);

  const goToPage = useCallback((p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const rangeStart = (safePage - 1) * ITEMS_PER_PAGE + 1;
  const rangeEnd = Math.min(safePage * ITEMS_PER_PAGE, filtered.length);

  return (
    <>
      <Hero>
        <HeroMark>◇</HeroMark>
        <HeroTitle>
          {config.siteTitle || 'Indic Knowledge Encyclopedia'}
        </HeroTitle>
        <HeroSub>{config.siteSubtitle || ''}</HeroSub>
        <ArticleCount>
          {(totalCount || manifest.length).toLocaleString()} article
          {(totalCount || manifest.length) !== 1 ? 's' : ''}
        </ArticleCount>
      </Hero>

      <SearchWrap>
        <SearchIcon>⌕</SearchIcon>
        <SearchInput
          type="text"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search articles..."
        />
        {search && <ClearBtn onClick={() => { setSearch(''); setPage(1); }}>&times;</ClearBtn>}
      </SearchWrap>

      {(search || activeLetter) && (
        <ResultCount>
          {filtered.length.toLocaleString()} result
          {filtered.length !== 1 ? 's' : ''}
          {search ? ` for "${search}"` : ''}
          {activeLetter ? ` in "${activeLetter}"` : ''}
        </ResultCount>
      )}

      {allLetters.length > 0 && (
        <AlphabetBar>
          <AllLink
            $active={!activeLetter}
            onClick={() => { setActiveLetter(null); setPage(1); }}
          >
            ALL
          </AllLink>
          {allLetters.map((l) => (
            <LetterLink
              key={l}
              $active={activeLetter === l}
              onClick={() => handleLetterClick(l)}
            >
              {l}
            </LetterLink>
          ))}
        </AlphabetBar>
      )}

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <ContentArea>
          {grouped.map((group) => (
            <LetterGroup key={group.letter} id={`letter-${group.letter}`}>
              <LetterHeading>{group.letter}</LetterHeading>
              <PageGrid>
                {group.pages.map((pg) => {
                  const approved = isPageProofread(pg.slug, adminState);
                  return (
                    <PageCard key={pg.slug} to={`/wiki/${encodeURIComponent(pg.slug)}`}>
                      <CardTitle>{pg.displayTitle}</CardTitle>
                      {approved ? (
                        <CardStatusApproved title="Proofread &amp; Verified">
                          ✓
                        </CardStatusApproved>
                      ) : (
                        <CardStatusPending title="Not yet proofread">
                          ○
                        </CardStatusPending>
                      )}
                    </PageCard>
                  );
                })}
              </PageGrid>
            </LetterGroup>
          ))}

          {filtered.length === 0 && (
            <NoResults>
              No articles found
              {search ? <> for &ldquo;{search}&rdquo;</> : ''}
              {activeLetter ? ` under "${activeLetter}"` : ''}
            </NoResults>
          )}

          {totalPages > 1 && (
            <>
              <PaginationWrap>
                <PageBtn
                  disabled={safePage === 1}
                  onClick={() => goToPage(safePage - 1)}
                >
                  ‹ Prev
                </PageBtn>
                {buildPageNumbers(safePage, totalPages).map((p, i) =>
                  p === '...' ? (
                    <PageEllipsis key={`e${i}`}>…</PageEllipsis>
                  ) : (
                    <PageBtn
                      key={p}
                      $active={p === safePage}
                      onClick={() => goToPage(p)}
                    >
                      {p}
                    </PageBtn>
                  ),
                )}
                <PageBtn
                  disabled={safePage === totalPages}
                  onClick={() => goToPage(safePage + 1)}
                >
                  Next ›
                </PageBtn>
              </PaginationWrap>
              <PageInfo>
                Showing {rangeStart.toLocaleString()}–
                {rangeEnd.toLocaleString()} of{' '}
                {filtered.length.toLocaleString()} articles
              </PageInfo>
            </>
          )}
        </ContentArea>
      )}
    </>
  );
}
